import * as React from 'react';

import {
  AsyncBoundary,
  Button,
  Col,
  ContentCard,
  ContentSection,
  Row,
  TextArea,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { cn } from '@rematter/ui-web/lib';
import { useAtom, useAtomValue } from 'jotai';
import {
  LucideAlertTriangle,
  LucideCheckCircle,
  LucideShieldAlert,
  LucideShieldCheck,
  LucideXCircle,
} from 'lucide-react';

import { ComplianceCheckRecord } from '../../_graphql/queryComplianceChecks';
import {
  $complianceChecks,
  $overrideComplianceCheck,
} from '../../_state/complianceDetail.state';
import { useTranslations } from '../../i18n/useTranslations';

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'Passed':
      return <LucideCheckCircle className="size-5 text-success" />;
    case 'Failed':
      return <LucideXCircle className="size-5 text-failure" />;
    case 'Warning':
      return <LucideAlertTriangle className="size-5 text-warning" />;
    case 'Overridden':
      return <LucideShieldCheck className="size-5 text-primary" />;
    default:
      return <LucideShieldAlert className="size-5 text-neutral/40" />;
  }
};

const statusColor = (status: string) => {
  switch (status) {
    case 'Passed':
      return 'text-success';
    case 'Failed':
      return 'text-failure';
    case 'Warning':
      return 'text-warning';
    case 'Overridden':
      return 'text-primary';
    default:
      return 'text-neutral/40';
  }
};

type CheckResultRowProps = {
  label: string;
  value: string | null | undefined;
};

const CheckResultRow = ({ label, value }: CheckResultRowProps) => (
  <Row className="@CheckResultRow items-center justify-between py-1.5 text-sm">
    <span className="text-neutral/60">{label}</span>
    <span>{value ?? '–'}</span>
  </Row>
);

type OverrideFormProps = {
  check: ComplianceCheckRecord;
  onSuccess: () => void;
};

const OverrideForm = ({ check, onSuccess }: OverrideFormProps) => {
  const t = useTranslations();
  const [{ isPending }, overrideCheck] = useAtom($overrideComplianceCheck);
  const toast = useToast();

  const [reason, setReason] = React.useState('');
  const [notes, setNotes] = React.useState('');

  const handleOverride = async () => {
    if (!reason) return;
    try {
      await overrideCheck({
        id: check.id,
        overrideReason: reason,
        notes,
      });
      toast.success(
        t.purchaseLoadDetail.compliance.checks.messages.overrideSuccess,
      );
      onSuccess();
    } catch (error) {
      toast.error(
        t.purchaseLoadDetail.compliance.checks.messages.overrideError(
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    }
  };

  return (
    <Col
      gap3
      className="@OverrideForm mt-3 rounded-lg border border-neutral/10 p-4"
    >
      <div className="text-sm font-medium">
        {t.purchaseLoadDetail.compliance.checks.overrideTitle}
      </div>
      <TextField
        label={t.purchaseLoadDetail.compliance.checks.fields.reason}
        value={reason}
        onChange={setReason}
        placeholder={t.purchaseLoadDetail.compliance.checks.placeholders.reason}
        isRequired
      />
      <TextArea
        label={t.purchaseLoadDetail.compliance.checks.fields.notes}
        value={notes}
        onChange={setNotes}
        placeholder={t.purchaseLoadDetail.compliance.checks.placeholders.notes}
      />
      <Row className="justify-end">
        <Button
          color="primary"
          variant="solid"
          size="small"
          isDisabled={!reason}
          isPending={isPending}
          isLoading={isPending}
          onPress={handleOverride}
        >
          {t.purchaseLoadDetail.compliance.checks.actions.override}
        </Button>
      </Row>
    </Col>
  );
};

export const ComplianceCheckPanel = () => {
  const t = useTranslations();

  const checksQuery = useAtomValue($complianceChecks);
  const checks = checksQuery.data ?? [];

  const [overridingCheckId, setOverridingCheckId] = React.useState<
    string | null
  >(null);

  const handleOverrideSuccess = () => {
    setOverridingCheckId(null);
  };

  return (
    <ContentCard noXPadding className="@ComplianceCheckPanel">
      <ContentSection
        variant="panel"
        title={t.purchaseLoadDetail.compliance.checks.title}
      >
        <AsyncBoundary
          loading={checksQuery.loadingState?.isInitialLoading}
          error={checksQuery.error}
        >
          {checks.length === 0 ? (
            <div className="py-8 text-center text-sm text-neutral/50">
              {t.purchaseLoadDetail.compliance.checks.empty}
            </div>
          ) : (
            <Col gap4>
              {checks.map((check) => (
                <Col
                  key={check.id}
                  gap2
                  className="rounded-lg border border-neutral/10 p-4"
                >
                  <Row className="items-center justify-between">
                    <Row gap2 className="items-center">
                      <StatusIcon status={check.status} />
                      <span
                        className={cn(
                          'text-sm font-semibold',
                          statusColor(check.status),
                        )}
                      >
                        {check.status}
                      </span>
                    </Row>
                    <span className="text-xs text-neutral/40">
                      {new Date(check.checkedAt).toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                  </Row>

                  {check.results && (
                    <Col className="mt-1 divide-y divide-neutral/5">
                      {Object.entries(check.results).map(([key, val]) => {
                        let display: string;
                        if (val === null || val === undefined) display = '–';
                        else if (typeof val === 'string') display = val;
                        else if (typeof val === 'boolean')
                          display = val ? t.common.yes : t.common.no;
                        else if (typeof val === 'number')
                          display = val.toLocaleString();
                        else display = String(val);
                        return (
                          <CheckResultRow
                            key={key}
                            label={key}
                            value={display}
                          />
                        );
                      })}
                    </Col>
                  )}

                  {check.overrideReason && (
                    <Col gap1 className="mt-2 rounded bg-primary/5 p-3">
                      <div className="text-xs font-medium text-primary">
                        {t.purchaseLoadDetail.compliance.checks.overriddenLabel}
                      </div>
                      <div className="text-sm">{check.overrideReason}</div>
                      {check.notes && (
                        <div className="text-xs text-neutral/60">
                          {check.notes}
                        </div>
                      )}
                    </Col>
                  )}

                  {check.status === 'Failed' && !overridingCheckId && (
                    <Row className="mt-2 justify-end">
                      <Button
                        size="small"
                        variant="outline"
                        color="primary"
                        onPress={() => setOverridingCheckId(check.id)}
                      >
                        {
                          t.purchaseLoadDetail.compliance.checks.actions
                            .override
                        }
                      </Button>
                    </Row>
                  )}

                  {overridingCheckId === check.id && (
                    <OverrideForm
                      check={check}
                      onSuccess={handleOverrideSuccess}
                    />
                  )}
                </Col>
              ))}
            </Col>
          )}
        </AsyncBoundary>
      </ContentSection>
    </ContentCard>
  );
};
